/**
 * "Lost your phone?": the ways back in the server offers on a 2FA prompt,
 * `recovery_options: [{type, label}]` on the login MFA response, the reset
 * link's `requirement: "mfa"` response, and a failed step-up (403
 * `step_up_failed`). Each is an API action, not a link
 * (docs/backend/account-security-api.md, "Lost device during a 2FA prompt").
 */

type ApiRecord = Record<string, unknown>;

export type RecoveryOptionType = 'backup_code' | 'session_approval' | 'identity_check' | 'recovery_request';

/** Where the prompt is: each context supports a subset (the server already filters, this guards). */
export type RecoveryContext = 'login' | 'reset' | 'step-up';

export interface RecoveryOptionView {
  type: RecoveryOptionType;
  /** The server's button text, or ours when it sent none. */
  label: string;
  detail: string;
  /** How long it usually takes ("1 min", "Same day"): the options are listed fastest first. */
  time: string;
}

const ORDER: RecoveryOptionType[] = ['backup_code', 'session_approval', 'identity_check', 'recovery_request'];

const SUPPORTED: Record<RecoveryContext, RecoveryOptionType[]> = {
  login: ['backup_code', 'session_approval', 'identity_check', 'recovery_request'],
  reset: ['backup_code', 'identity_check', 'recovery_request'],
  'step-up': ['backup_code', 'session_approval', 'recovery_request'],
};

/** Fastest first: the order is the advice. */
const COPY: Record<RecoveryOptionType, { label: string; detail: string; time: string }> = {
  backup_code: { label: 'Use a backup code', detail: 'One of the codes you saved when you set up sign-in.', time: '1 min' },
  session_approval: { label: 'Approve from another device', detail: 'A computer or phone where you’re still signed in.', time: '1 min' },
  identity_check: { label: 'Use a photo of your ID', detail: 'Snap your driver’s license and a quick selfie.', time: '2 min' },
  recovery_request: { label: 'Ask your dealership admin', detail: 'They approve it. We email you both at each step.', time: 'Same day' },
};

/** `recovery_options` as views in a fixed order: known types only, once each, supported in this context. */
export function recoveryOptions(raw: unknown, context: RecoveryContext): RecoveryOptionView[] {
  if (!Array.isArray(raw)) return [];
  const labels = new Map<RecoveryOptionType, string>();
  for (const entry of raw) {
    const obj = entry && typeof entry === 'object' ? (entry as ApiRecord) : null;
    const type = typeof entry === 'string' ? entry : obj ? obj['type'] : null;
    if (typeof type !== 'string' || SUPPORTED[context].indexOf(type as RecoveryOptionType) === -1 || labels.has(type as RecoveryOptionType)) continue;
    const label = obj && typeof obj['label'] === 'string' ? (obj['label'] as string).trim() : '';
    labels.set(type as RecoveryOptionType, label);
  }
  return ORDER.filter((type) => labels.has(type)).map((type) => ({ type, label: labels.get(type) || COPY[type].label, detail: COPY[type].detail, time: COPY[type].time }));
}

/** `{recovery_request: "started"}` from any lost-device call. */
export function recoveryRequestStarted(data: unknown): boolean {
  return !!data && typeof data === 'object' && (data as ApiRecord)['recovery_request'] === 'started';
}

/** The capture token of a lost-device ID check: `identity_check.capture_token` (login) or `capture_token` (reset link). */
export function lostDeviceCaptureToken(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const body = data as ApiRecord;
  const nested = body['identity_check'] && typeof body['identity_check'] === 'object' ? (body['identity_check'] as ApiRecord)['capture_token'] : null;
  const token = typeof nested === 'string' ? nested : body['capture_token'];
  return typeof token === 'string' && token ? token : null;
}

/** What the person reads once a recovery request has started. Never says whether the account qualified. */
export const RECOVERY_REQUEST_STARTED =
  'Request started. We’ve emailed you and your dealership admins. Once an admin approves it, you’ll get an email to set up two-step verification again.';
